import { useState, useEffect } from "react";
import { UserName } from "@/components/badges/UserName";
import { useAuth } from "@/lib/auth";
import { useMessageThreads, useGetOrCreateThread, MessageThread } from "@/hooks/useMessageThreads";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { getStorageUrl } from "@/lib/storage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, MessageSquare, Plus, MoreVertical, Trash2, BookOpen } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ThreadMessagingPanel } from "./ThreadMessagingPanel";
import { MentorshipThreadPanel } from "@/components/mentorship/MentorshipThreadPanel";
import { format } from "date-fns";
import { toast } from "sonner";

interface MessageThreadsListProps {
  initialThreadId?: string | null;
  initialAction?: string | null;
}

export function MessageThreadsList({ initialThreadId, initialAction }: MessageThreadsListProps) {
  const { user } = useAuth();
  const { threads, isLoading, deleteThread } = useMessageThreads();
  const getOrCreateThread = useGetOrCreateThread();
  
  const [selectedThread, setSelectedThread] = useState<MessageThread | null>(null);
  const [newMessageDialogOpen, setNewMessageDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<string | null>(null);
  const [hasAttemptedAutoSelect, setHasAttemptedAutoSelect] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);

  // Log initial state for debugging
  useEffect(() => {
    if (initialThreadId) {
      console.log("[MessageThreadsList] Initial thread ID received:", initialThreadId);
      setDebugInfo(`Received initialThreadId: ${initialThreadId}`);
    }
  }, [initialThreadId]);

  // Fetch thread details if initialThreadId is provided
  const { data: initialThreadData, isLoading: isLoadingInitialThread, error: initialThreadError } = useQuery({
    queryKey: ["thread-by-id", initialThreadId],
    queryFn: async () => {
      if (!initialThreadId || !user?.id) return null;

      console.log("[MessageThreadsList] Fetching thread by ID:", initialThreadId);
      setDebugInfo(prev => `${prev}\nFetching thread...`);

      const { data: thread, error } = await supabase
        .from("message_threads")
        .select(`
          id,
          participant_1_id,
          participant_2_id,
          created_at,
          updated_at,
          thread_type,
          mentorship_request_id,
          participant_1:profiles!message_threads_participant_1_id_fkey(id, full_name, avatar_url),
          participant_2:profiles!message_threads_participant_2_id_fkey(id, full_name, avatar_url)
        `)
        .eq("id", initialThreadId)
        .maybeSingle();

      if (error) {
        console.error("[MessageThreadsList] Error fetching thread:", error);
        setDebugInfo(prev => `${prev}\nError: ${error.message}`);
        throw error;
      }
      
      if (!thread) {
        console.warn("[MessageThreadsList] Thread not found:", initialThreadId);
        setDebugInfo(prev => `${prev}\nThread not found!`);
        return null;
      }

      console.log("[MessageThreadsList] Thread fetched:", thread);
      setDebugInfo(prev => `${prev}\nThread found! Type: ${(thread as any).thread_type || 'general'}`);

      const isParticipant1 = thread.participant_1_id === user.id;
      const otherParticipant = isParticipant1 
        ? (Array.isArray(thread.participant_2) ? thread.participant_2[0] : thread.participant_2)
        : (Array.isArray(thread.participant_1) ? thread.participant_1[0] : thread.participant_1);

      return {
        id: thread.id,
        participant_1_id: thread.participant_1_id,
        participant_2_id: thread.participant_2_id,
        created_at: thread.created_at,
        updated_at: thread.updated_at,
        thread_type: (thread as any).thread_type || 'general',
        mentorship_request_id: (thread as any).mentorship_request_id || null,
        otherParticipant: otherParticipant || { id: "", full_name: "Unknown", avatar_url: null },
        lastMessage: null,
        unreadCount: 0,
      } as MessageThread;
    },
    enabled: !!initialThreadId && !!user?.id,
    retry: 2,
  });

  // Auto-select thread when data is available - only attempt once per initialThreadId
  useEffect(() => {
    // Skip if no initialThreadId or already selected or already attempted
    if (!initialThreadId || selectedThread || hasAttemptedAutoSelect) {
      return;
    }

    // Wait for loading to complete
    if (isLoadingInitialThread || isLoading) {
      return;
    }

    setHasAttemptedAutoSelect(true);

    // First check if thread is in the loaded list
    const threadFromList = threads.find(t => t.id === initialThreadId);
    if (threadFromList) {
      console.log("[MessageThreadsList] Found thread in list, selecting:", threadFromList.id);
      setDebugInfo(prev => `${prev}\nSelected from list (type: ${threadFromList.thread_type})`);
      setSelectedThread(threadFromList);
      return;
    }

    // If not in list but we have fetched it directly, use that
    if (initialThreadData) {
      console.log("[MessageThreadsList] Using directly fetched thread:", initialThreadData.id);
      setDebugInfo(prev => `${prev}\nSelected from direct fetch (type: ${initialThreadData.thread_type})`);
      setSelectedThread(initialThreadData);
      return;
    }

    // If we got here, thread wasn't found
    if (initialThreadError) {
      console.error("[MessageThreadsList] Failed to load thread:", initialThreadError);
      setDebugInfo(prev => `${prev}\nFailed to load: ${initialThreadError.message}`);
      toast.error("Could not open conversation. The thread may not exist or you may not have access.");
    } else {
      console.warn("[MessageThreadsList] Thread not found in list or direct fetch");
      setDebugInfo(prev => `${prev}\nThread not found anywhere`);
    }
  }, [initialThreadId, threads, selectedThread, initialThreadData, hasAttemptedAutoSelect, isLoadingInitialThread, isLoading, initialThreadError]);

  // Reset attempt flag when initialThreadId changes
  useEffect(() => {
    setHasAttemptedAutoSelect(false);
    setDebugInfo(null);
  }, [initialThreadId]);

  // Get list of users with accepted connections (can message)
  const { data: connectableUsers } = useQuery({
    queryKey: ["connectable-users", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Query accepted connections from the unified connections table
      const { data: acceptedConnections, error: connError } = await supabase
        .from("connections")
        .select("requester_id, recipient_id")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .eq("status", "accepted");

      // Also query accepted mentorship requests
      const { data: acceptedMentorships, error: mentorError } = await supabase
        .from("mentorship_requests")
        .select("entrepreneur_id, mentor_id")
        .or(`entrepreneur_id.eq.${user.id},mentor_id.eq.${user.id}`)
        .eq("status", "accepted");

      if (connError) throw connError;

      // Collect all other-user IDs
      const otherUserIds = new Set<string>();

      acceptedConnections?.forEach((conn) => {
        if (conn.requester_id === user.id) otherUserIds.add(conn.recipient_id);
        else otherUserIds.add(conn.requester_id);
      });

      acceptedMentorships?.forEach((m) => {
        if (m.entrepreneur_id === user.id) otherUserIds.add(m.mentor_id);
        else otherUserIds.add(m.entrepreneur_id);
      });

      if (otherUserIds.size === 0) return [];

      // Fetch profiles for all connectable users
      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", Array.from(otherUserIds));

      if (profileError) throw profileError;

      return profiles || [];
    },
    enabled: !!user?.id,
  });

  const handleStartConversation = async (otherUserId: string) => {
    try {
      const threadId = await getOrCreateThread.mutateAsync(otherUserId);
      const otherUser = connectableUsers?.find((u) => u.id === otherUserId);
      
      if (otherUser) {
        setSelectedThread({
          id: threadId,
          participant_1_id: user?.id || "",
          participant_2_id: otherUserId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          thread_type: 'general',
          mentorship_request_id: null,
          otherParticipant: otherUser,
          lastMessage: null,
          unreadCount: 0,
        });
      }
      
      setNewMessageDialogOpen(false);
    } catch (error) {
      toast.error("Failed to start conversation");
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    setThreadToDelete(threadId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!threadToDelete) return;

    try {
      await deleteThread.mutateAsync(threadToDelete);
      toast.success("Conversation deleted");
      
      if (selectedThread?.id === threadToDelete) {
        setSelectedThread(null);
      }
    } catch (error) {
      toast.error("Failed to delete conversation");
    } finally {
      setDeleteDialogOpen(false);
      setThreadToDelete(null);
    }
  };

  // Check if a non-mentorship thread actually has an accepted mentorship between participants
  // NOTE: This hook MUST be called before any conditional returns to satisfy React's Rules of Hooks
  const { data: hasMentorshipForThread } = useQuery({
    queryKey: ["thread-mentorship-check", selectedThread?.id, selectedThread?.participant_1_id, selectedThread?.participant_2_id],
    queryFn: async () => {
      if (!selectedThread) return false;
      // Already identified as mentorship - no need to check
      if (selectedThread.thread_type === 'mentorship' || selectedThread.mentorship_request_id) return true;

      const { data } = await supabase
        .from("mentorship_requests")
        .select("id")
        .eq("status", "accepted")
        .or(
          `and(mentor_id.eq.${selectedThread.participant_1_id},entrepreneur_id.eq.${selectedThread.participant_2_id}),and(mentor_id.eq.${selectedThread.participant_2_id},entrepreneur_id.eq.${selectedThread.participant_1_id})`
        )
        .limit(1)
        .maybeSingle();

      return !!data;
    },
    enabled: !!selectedThread && selectedThread.thread_type !== 'mentorship' && !selectedThread.mentorship_request_id,
  });

  // Show loading if fetching threads OR fetching initial thread by ID
  if (isLoading || (initialThreadId && isLoadingInitialThread && !selectedThread)) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (selectedThread) {
    // Route to MentorshipThreadPanel if thread is mentorship type, has a mentorship_request_id,
    // or if an accepted mentorship_request exists between the two participants
    const isMentorshipThread = 
      selectedThread.thread_type === 'mentorship' || 
      selectedThread.mentorship_request_id ||
      hasMentorshipForThread;

    if (isMentorshipThread) {
      return (
        <MentorshipThreadPanel
          threadId={selectedThread.id}
          otherPerson={selectedThread.otherParticipant}
          onBack={() => setSelectedThread(null)}
          autoOpenSchedule={initialAction === 'schedule'}
        />
      );
    }
    
    // Regular threads use ThreadMessagingPanel
    return (
      <ThreadMessagingPanel
        threadId={selectedThread.id}
        otherPerson={selectedThread.otherParticipant}
        onBack={() => setSelectedThread(null)}
      />
    );
  }

  if (threads.length === 0 && (!connectableUsers || connectableUsers.length === 0)) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No active connections to message</p>
          <p className="text-sm text-muted-foreground">Connect with a mentor or entrepreneur to start messaging</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* New Message Button */}
      {connectableUsers && connectableUsers.length > 0 && (
        <Dialog open={newMessageDialogOpen} onOpenChange={setNewMessageDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              New Message
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Start a conversation</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-[400px] overflow-y-auto py-2">
              {connectableUsers.map((person) => (
                <Card
                  key={person.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleStartConversation(person.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={getStorageUrl("avatars", person.avatar_url) || undefined} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {person.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium"><UserName userId={person.id} name={person.full_name} /></p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Threads List */}
      {threads.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No conversations yet</p>
            <p className="text-sm text-muted-foreground">Start a new conversation with your connections</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {threads.map((thread) => (
            <Card key={thread.id} className="hover:bg-muted/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={getStorageUrl("avatars", thread.otherParticipant.avatar_url) || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {thread.otherParticipant.full_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium"><UserName userId={thread.otherParticipant.id} name={thread.otherParticipant.full_name} /></p>
                      {(thread.thread_type === 'mentorship' || thread.mentorship_request_id) && (
                        <Badge variant="secondary" className="text-xs">
                          <BookOpen className="h-3 w-3 mr-1" />
                          Mentorship
                        </Badge>
                      )}
                      {thread.unreadCount > 0 && (
                        <Badge className="bg-primary text-primary-foreground">
                          {thread.unreadCount} new
                        </Badge>
                      )}
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedThread(thread);
                          }}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" />
                          Message
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => handleDeleteClick(e as unknown as React.MouseEvent, thread.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete conversation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    {thread.lastMessage ? (
                      <p className="text-sm text-muted-foreground truncate mt-1">
                        {thread.lastMessage.sender_id === user?.id ? "You: " : ""}
                        {thread.lastMessage.content}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground mt-1">No messages yet - start a conversation</p>
                    )}
                  </div>
                  {thread.lastMessage && (
                    <span className="text-xs text-muted-foreground shrink-0 hidden sm:block">
                      {format(new Date(thread.lastMessage.created_at), "MMM d")}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all messages in this conversation. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
